import { useCurrentVarsityApp } from "@/composables/useCurrentVarsityApp"
import { useGlobalState, type GlobalState } from "@/stores/useGlobalState"
import { useAppConfig, type AppConfigMenuNode } from "@/vf"
import { storeToRefs } from "pinia"
import { computed, ref, unref, watch, type Ref } from "vue"
import { useRouter } from "vue-router"

export interface VfMenuItem extends AppConfigMenuNode {
    // patched in menu builder
    hasActiveChildren: boolean
    children?: VfMenuItem[]
}

interface VfRoutableMenuItem extends VfMenuItem {
    url: string
}

export function useMenuBuilder() {
    const router = useRouter()
    const { appConfig } = useAppConfig()

    const activeMenuItem = ref<VfMenuItem | null>(null)
    const menuItems: Ref<VfMenuItem[]> = ref([])
    const routableMenuItems = ref<VfRoutableMenuItem[]>([])
    const { globalState }: { globalState: Ref<GlobalState> } = storeToRefs(useGlobalState())

    function onMenuChange(cb: () => any) {
        watch(appConfig, cb)
        watch(router.currentRoute, cb)

        cb()
    }

    onMenuChange(update)

    function update() {
        if (!appConfig.value.menu) {
            return
        }

        menuItems.value = _convertMenuItems(appConfig.value.menu)

        // create a flat list of all menu items that can be navigated to and resolve the url
        routableMenuItems.value = _prepareRoutableMenuItems(menuItems.value)

        // find the best matched menu item by url (see _getActiveItemBestMatchByUrl)
        activeMenuItem.value = _getActiveItemBestMatchByUrl(router.currentRoute.value.fullPath)

        // flag the menu hierarchy that leads to the best match with the `hasActiveChildren` flag
        _fillHasActiveChildren(menuItems.value)
    }

    function _convertMenuItems(menu: AppConfigMenuNode[]): VfMenuItem[] {
        return menu.map(node => {
            const item: VfRoutableMenuItem = {
                id: node.id,
                label: node.label,
                params: node.params,
                settings: node.settings,
                route: node.route,

                // url is required to find the best matched menu item (see _getActiveItemBestMatchByUrl)
                url: "",
                hasActiveChildren: false,
            }

            if (node.children && node.children.length > 0) {
                item.children = _convertMenuItems(node.children)
            }

            return item
        })
    }

    /**
     * Creates a flat list of all routable menu items
     */
    function _prepareRoutableMenuItems(menu: VfMenuItem[]): VfRoutableMenuItem[] {
        let elements: VfRoutableMenuItem[] = []

        for (const item of menu) {
            const routableItem = { ...item } as VfRoutableMenuItem
            delete routableItem.children

            if (item.children && item.children.length > 0) {
                elements = elements.concat(_prepareRoutableMenuItems(item.children))
            }

            if (item.route) {
                routableItem.url = router.resolve({ name: item.route, params: item.params }).href

                // required if navigation is not done via pushState()
                if (routableItem.url.startsWith("#")) {
                    routableItem.url = routableItem.url.substring(1)
                }

                elements.push(routableItem)
            }
        }

        return elements
    }

    /*─────────────────────────────────────┐
    │        find active menu item         │
    └─────────────────────────────────────*/
    function _getActiveItemBestMatchByUrl(currentUrl: string): VfMenuItem {
        const bestMatch: { length: number; item: VfMenuItem | null } = { length: 0, item: null }

        for (const menuItem of routableMenuItems.value) {
            if (!currentUrl.startsWith(menuItem.url)) {
                continue
            }

            if (currentUrl === menuItem.url) {
                return menuItem
            }

            if (menuItem.url.length > bestMatch.length) {
                bestMatch.length = menuItem.url.length
                bestMatch.item = menuItem
            }
        }

        return bestMatch.item!
    }

    /**
     * Updates the `hasActiveChildren` flag of all menu items and submenu items
     */
    function _fillHasActiveChildren(menu: VfMenuItem[]) {
        for (const child of menu) {
            child.hasActiveChildren = elementsContainElement(child, activeMenuItem.value)

            if (child.children) {
                _fillHasActiveChildren(child.children)
            }
        }
    }

    function getFirstChildRoutingData(
        itemRef: Ref<VfMenuItem> | VfMenuItem,
    ): { name: string; params: Record<string, string> } | null {
        const item = unref(itemRef)

        if (!item) {
            return null
        }

        if (item.route) {
            return { name: item.route, params: item.params }
        }

        if (!item.children) {
            throw new Error("Menu item has no route and no children")
        }

        for (const child of item.children) {
            if (child.route) {
                // const preparedParams = {}

                // for (const [key, value] of Object.entries(child.params)) {
                //     let newValue: string | string[] = value

                //     if (value?.includes("/")) {
                //         newValue = value.split("/")

                //         console.log("replace path params", value, newValue)
                //     }

                //     preparedParams[key] = newValue
                // }

                return { name: child.route, params: child.params }
            }

            if (child.children && child.children.length > 0) {
                return getFirstChildRoutingData(child)
            }
        }

        return null
    }

    function getRootMenu(menuId: string): VfMenuItem | undefined {
        return menuItems.value.filter(menuItem => menuItem.id === menuId).pop()
    }

    function countTodos(item: VfMenuItem | undefined): number {
        if (!item) {
            return 0
        }

        const walkDown = (item: VfMenuItem): number => {
            const my = globalState.value?.todos?.find(i => i.route === item.route)?.count ?? 0

            let childrenCount = 0
            if (item.children) {
                childrenCount = item.children.reduce((acc, child) => acc + walkDown(child), 0)
            }

            return my + childrenCount
        }
        return walkDown(item)
    }

    function isMenuItemVisible(item: VfMenuItem, menu: number = 1): boolean {
        const id = item.id || item.route

        if (!item.label || !id) {
            return false
        }

        //settings.hidden can be true/false or an array containing the menus in which this item should be hidden
        if ("settings" in item && "hidden" in item.settings) {
            if (
                item.settings.hidden === true ||
                (Array.isArray(item.settings.hidden) && item.settings.hidden.includes(menu))
            ) {
                return false
            }
        }
        return true
    }

    const myOfficeRoute = computed(() => {
        const menu = getRootMenu("my-office")
        if (!menu) {
            return null
        }

        return getFirstChildRoutingData(menu)
    })

    const myAdminRoute = computed(() => {
        const menu = getRootMenu("my-admin")
        if (!menu) {
            return null
        }

        return getFirstChildRoutingData(menu)
    })

    const myAccountingRoute = computed(() => {
        const menu = getRootMenu("my-accounting")
        if (!menu) {
            return null
        }

        return getFirstChildRoutingData(menu)
    })

    const devToolsRoute = computed(() => {
        const menu = getRootMenu("dev-tools")
        if (!menu) {
            return null
        }

        return getFirstChildRoutingData(menu)
    })

    const myStaffRoute = computed(() => {
        const menu = getRootMenu("my-staff")
        if (!menu) {
            return null
        }

        return getFirstChildRoutingData(menu)
    })

    const myScoringRoute = computed(() => {
        const menu = getRootMenu("my-scoring")
        if (!menu) {
            return null
        }

        return getFirstChildRoutingData(menu)
    })

    const { currentVarsityApp } = useCurrentVarsityApp()

    return {
        activeMenuItem,
        getFirstChildRoutingData,
        menuItems,
        onMenuChange,
        routableMenuItems,
        getRootMenu,
        myOfficeRoute,
        myAccountingRoute,
        myAdminRoute,
        devToolsRoute,
        myStaffRoute,
        myScoringRoute,
        currentVarsityApp,
        countTodos,
        isMenuItemVisible,
    } as const
}

function elementsContainElement(element: VfMenuItem, searchedElement: VfMenuItem | null) {
    if (!searchedElement) {
        return false
    }

    if (element.route === searchedElement.route && element.params == searchedElement.params) {
        return true
    }

    // items that are not part of the navigation can mark another item as active in their place
    if (searchedElement.settings?.activeMenuItem && element.route === searchedElement.settings.activeMenuItem) {
        return true
    }

    if (element.children && element.children.length) {
        for (const child of element.children) {
            if (elementsContainElement(child, searchedElement)) {
                return true
            }
        }
    }

    return false
}
